import { DraftIcpDto, IcpSegmentDto, IcpStateDto } from "@growth/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

/** The active brand's ideal customer profile (real, from the server). Polls while drafting. */
export const useIcp = () =>
  useQuery({
    queryKey: ["icp"],
    queryFn: async () => (await api.get<IcpStateDto>("/leads/icp")).data,
    refetchInterval: (query) => (query.state.data?.pending?.status === "drafting" ? 2500 : false),
  });

const useIcpMutation = <T>(fn: (input: T) => Promise<IcpStateDto>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (state) => queryClient.setQueryData(["icp"], state),
  });
};

export const useStartIcpDraft = () =>
  useIcpMutation(
    async (input: DraftIcpDto) => (await api.post<IcpStateDto>("/leads/icp/draft", input)).data,
  );

export const useRedoIcp = () =>
  useIcpMutation(
    async (note: string) => (await api.post<IcpStateDto>("/leads/icp/draft/redo", { note })).data,
  );

export const useSaveSegment = () =>
  useIcpMutation(
    async (segment: IcpSegmentDto) =>
      (
        await api.put<IcpStateDto>(`/leads/icp/draft/segments/${encodeURIComponent(segment.id)}`, {
          segment,
        })
      ).data,
  );

export const useConfirmIcp = () =>
  useIcpMutation(
    async (version: number) =>
      (await api.post<IcpStateDto>("/leads/icp/draft/confirm", { version })).data,
  );

export const useDiscardIcpDraft = () =>
  useIcpMutation(async (_: void) => (await api.delete<IcpStateDto>("/leads/icp/draft")).data);
